import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { EmptyLayout, Table } from "../../components/molecules";
import { databaseIcon, deleteIcon, editIcon } from "../../assets/icons";
import {
  fetchENVSecrets,
  selectSelectedSecrets,
  deleteSecret,
  setSelectedDataModelId,
  selectSelectedDataModel,
} from "./index";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { getFormattedDate } from "../../helpers";
import { actions as dataModelActions } from "./dataModels.reducers";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";

const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedSecretKey } = dataModelActions;
const { ADD_SECRETS_MODAL, DELETE_MODAL } = MODALS;
const { FETCH_SECRETS, DELETE_SECRET } = SPINNER_NAMES;
const { setSelectedTab, setSelectedSubTab } = breadCrumbActions;
const SECRETS = "Secrets";

const ManageSecretsModal = () => {
  const dispatch = useDispatch();
  const { dataModelId } = useParams();
  const selectedSecrets = useSelector(selectSelectedSecrets);
  const dataModel = useSelector(selectSelectedDataModel);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);

  useEffect(() => {
    if (dataModelId) dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId]);

  useEffect(() => {
    if (dataModel) {
      dispatch(setSelectedTab(dataModel));
      dispatch(setSelectedSubTab(SECRETS));
      return () => dispatch(setSelectedSubTab());
    }
  }, [dispatch, dataModel]);

  const handleDeleteSecretKey = useCallback(
    (key) => {
      dispatch(deleteSecret(key.id)).then((res) => {
        if (res.payload) {
          setShouldShowDeleteModal(false);
          dispatch(fetchENVSecrets(dataModelId));
        }
      });
    },
    [dispatch, dataModelId]
  );

  const buttonFormatter = (row) => (
    <div className="flex">
      <div
        className="rounded bg-gray-900 w-8 h-8 m-1 flex justify-center items-center"
        onClick={() => {
          dispatch(setSelectedModal(ADD_SECRETS_MODAL));
          dispatch(setSelectedSecretKey(row));
        }}
      >
        <img src={editIcon} alt="edit-icon" />
      </div>
      <div
        className="rounded bg-gray-900 w-8 h-8 m-1 flex justify-center items-center "
        onClick={() => {
          setShouldShowDeleteModal(true);
          dispatch(setItemToBeDeleted(row));
          dispatch(setSelectedModal(DELETE_MODAL));
        }}
      >
        <img src={deleteIcon} alt="delete-icon" />
      </div>
    </div>
  );

  const dateFormatter = (row) => getFormattedDate(row.updatedAt);

  const columns = [
    {
      dataField: "name",
      text: "Name",
    },
    {
      text: "Updated At",
      className: "text-center",
      formatter: dateFormatter,
    },
    {
      formatter: buttonFormatter,
      width: 30,
    },
  ];

  const handleShouldShowDrawer = () =>
    dispatch(setSelectedModal(ADD_SECRETS_MODAL));

  const renderSecrets = () =>
    selectedSecrets.length ? (
      <>
        <Table data={selectedSecrets} columns={columns} />
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteSecretKey}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Secret"
            spinner={DELETE_SECRET}
          />
        )}
      </>
    ) : (
      <EmptyLayout
        title="No Secret"
        subTitle="Add a new Secret key."
        buttonText="Create New Secret"
        image={databaseIcon}
        handleOnClick={handleShouldShowDrawer}
      />
    );

  return (
    <div className="content scrollbar bg-gray-900 overflow-auto p-6 flex flex-1 flex-col">
      <Helmet>
        <title> Secrets | TavaJS</title>
      </Helmet>
      <div className="flex-col flex mb-4 gap-4">
        <div className="flex-col  sm:flex-row flex">
          <div className="sm:ml-auto flex flex-row mt-3 sm:mt-0">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleShouldShowDrawer()}
            >
              Add New Secret
            </button>
          </div>
        </div>
      </div>
      <div className="justify-between flex flex-col flex-1">
        <Spinner name={FETCH_SECRETS}>{renderSecrets()}</Spinner>
      </div>
    </div>
  );
};

export default ManageSecretsModal;
